import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getEmployees } from '../../hooks/employees';
import { saveTask } from '../../hooks/tasks';
import { Employee } from '../../utils/types';
import {
  defaultTaskFormValues,
  TaskFormFields,
  TaskFormValues,
} from './components/taskFormShared';
import {
  formatTaskDate,
  TaskHero,
  TaskPrimaryButton,
  TaskSecondaryButton,
  TaskSectionCard,
  TaskShell,
} from './components/taskUi';

const AddTaskPage = () => {
  const toast = useToast();
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<TaskFormValues>(defaultTaskFormValues);

  useEffect(() => {
    const getEmployeesData = async () => {
      if (!orgId) {
        return;
      }

      setLoadingEmployees(true);
      const response = await getEmployees(orgId);
      if (response.success === true && Array.isArray(response.data)) {
        setEmployees(response.data);
      } else {
        setEmployees([]);
        toast({
          title: 'Could not load employees',
          description: 'Assignee options are not available right now.',
          status: 'warning',
          duration: 3500,
          isClosable: true,
        });
      }
      setLoadingEmployees(false);
    };

    getEmployeesData();
  }, [orgId, toast]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!orgId) {
      return;
    }

    setSaving(true);
    const response = await saveTask(
      orgId,
      formData.title,
      formData.description,
      formData.start_date,
      formData.due_date,
      formData.priority,
      formData.status,
      formData.assigned_to || null,
      formData.remarks,
    );
    setSaving(false);

    if (response?.success === true) {
      toast({
        title: 'Task created',
        description: `${formData.title} is now in the queue.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setFormData(defaultTaskFormValues);
      navigate(`/organization/${orgId}/tasks/${response?.data?.id}/view`);
    } else {
      toast({
        title: 'Could not create task',
        description: response?.data?.detail || 'Please review the form and try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <TaskShell compact>
      <TaskHero
        eyebrow="New task"
        title="Create a task with clear ownership and timing"
        description="Keep the brief tight, assign the right owner, and set a due date that makes the work visible."
        primaryAction={
          <TaskPrimaryButton
            leftIcon={<AddIcon />}
            onClick={() => formRef.current?.requestSubmit()}
            isLoading={saving}
            isDisabled={loadingEmployees}
          >
            Create task
          </TaskPrimaryButton>
        }
        secondaryAction={
          <TaskSecondaryButton onClick={() => navigate(-1)}>
            Cancel
          </TaskSecondaryButton>
        }
      />

      <form ref={formRef} onSubmit={handleSubmit}>
        <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="start">
          <Box gridColumn={{ xl: 'span 2' }}>
            <TaskFormFields
              values={formData}
              employees={employees}
              onChange={handleChange}
            />
          </Box>

          <VStack align="stretch" spacing={5}>
            <TaskSectionCard
              title="Task summary"
              description="A quick read of the task before you save it."
            >
              <Stack spacing={4}>
                <Box>
                  <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.12em" fontWeight="800" color="gray.500" mb={1}>
                    Title
                  </Text>
                  <Text fontWeight="700" color="gray.900">
                    {formData.title || 'Untitled task'}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.12em" fontWeight="800" color="gray.500" mb={1}>
                    Assignee
                  </Text>
                  <Text color="gray.700">
                    {employees.find((employee) => employee.id === formData.assigned_to)?.user?.first_name || 'Not assigned yet'}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.12em" fontWeight="800" color="gray.500" mb={1}>
                    Due
                  </Text>
                  <Text color="gray.700">{formatTaskDate(formData.due_date)}</Text>
                </Box>
              </Stack>
            </TaskSectionCard>

            <TaskSectionCard
              title="Creation guidance"
              description="Keep the task lightweight, but specific enough to be actioned without follow-up."
            >
              <Stack spacing={3}>
                <Text fontSize="sm" color="gray.600">
                  Good task titles say exactly what needs to happen.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Due dates and ownership are the two biggest drivers of accountability.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Use remarks for internal handoff detail or blockers.
                </Text>
              </Stack>
            </TaskSectionCard>

            <Button
              type="submit"
              bg="#166534"
              color="white"
              borderRadius="16px"
              _hover={{ bg: '#14532D' }}
              isLoading={saving}
              isDisabled={loadingEmployees}
              display={{ base: 'inline-flex', xl: 'none' }}
            >
              Create task
            </Button>
          </VStack>
        </SimpleGrid>
      </form>
    </TaskShell>
  );
};

export default AddTaskPage;
